"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { speakerHueStyle } from "@/lib/utils/speaker-color";
import type { LiveParagraph } from "../hooks/use-live-transcript";

const NO_SPEECH_THRESHOLD_MS = 5000; // ms after connect before flipping empty-state copy

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

interface LiveTranscriptProps {
  paragraphs: LiveParagraph[];
  isConnected: boolean;
  isComplete: boolean;
  error: string | null;
  reconnectStatus?: "idle" | "reconnecting" | "exhausted";
  className?: string;
}

export function LiveTranscript({
  paragraphs,
  isConnected,
  isComplete,
  error,
  reconnectStatus = "idle",
  className,
}: LiveTranscriptProps) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);

  // Reset on disconnect so a reconnect restarts the 5s grace window.
  const [connectedSinceMs, setConnectedSinceMs] = useState<number | null>(null);
  // One-shot setTimeout forces a re-render after NO_SPEECH_THRESHOLD_MS to flip empty-state copy.
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (isConnected) {
      setConnectedSinceMs(Date.now());
    } else {
      setConnectedSinceMs(null);
    }
  }, [isConnected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [paragraphs]);

  useEffect(() => {
    if (
      connectedSinceMs === null ||
      paragraphs.length > 0 ||
      reconnectStatus !== "idle"
    ) {
      return;
    }
    const remaining = NO_SPEECH_THRESHOLD_MS - (Date.now() - connectedSinceMs);
    if (remaining <= 0) return;
    const id = setTimeout(() => forceTick((n) => n + 1), remaining + 50);
    return () => clearTimeout(id);
  }, [connectedSinceMs, paragraphs.length, reconnectStatus]);

  if (error) {
    return (
      <div
        className={cn(
          "rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger",
          className,
        )}
      >
        <p className="font-medium">{t("RECORD.LIVE_ERROR_TITLE")}</p>
        <p>{error}</p>
      </div>
    );
  }

  const showExhausted = reconnectStatus === "exhausted";
  const showReconnecting = !showExhausted && reconnectStatus === "reconnecting";
  const elapsedSinceConnect =
    connectedSinceMs !== null ? Date.now() - connectedSinceMs : 0;
  const showNoSpeechCopy = elapsedSinceConnect > NO_SPEECH_THRESHOLD_MS;

  return (
    <div
      // role="log" implies aria-live="polite" + aria-atomic="false". Belt-and-suspenders
      // aria-live="polite" added for browser compatibility. Do NOT add a second role="log"
      // or aria-live to this subtree from the parent.
      role="log"
      aria-live="polite"
      aria-label={t("RECORD.LIVE_TRANSCRIPT_REGION_LABEL", "Live transcript")}
      className={cn(
        "flex flex-col gap-3 overflow-y-auto rounded-lg border border-border bg-card p-4",
        className,
      )}
    >
      {showExhausted && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="rounded-lg border border-muted bg-muted/30 p-3"
        >
          <p className="text-sm text-muted-foreground">
            {t(
              "RECORD.LIVE_EXHAUSTED",
              "Live transcript paused, connection lost. Your recording continues normally and will be transcribed after upload.",
            )}
          </p>
        </div>
      )}

      {showReconnecting && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3"
        >
          <RotateCw
            className="h-4 w-4 flex-none text-warning animate-spin"
            aria-hidden="true"
          />
          {/* text-warning (not text-warning-foreground) for WCAG AA on bg-warning/10 in dark mode */}
          <p className="text-sm text-warning">
            {t("RECORD.LIVE_RECONNECTING", "Reconnecting transcription…")}
          </p>
        </div>
      )}

      {!showExhausted && !showReconnecting && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className={cn(
              "inline-block h-2 w-2 rounded-full",
              isConnected
                ? "animate-pulse bg-success"
                : isComplete
                  ? "bg-muted-foreground"
                  : "bg-warning animate-pulse",
            )}
          />
          <span>
            {isConnected
              ? t("RECORD.LIVE_ACTIVE")
              : isComplete
                ? t("RECORD.LIVE_COMPLETE")
                : t("RECORD.LIVE_CONNECTING")}
          </span>
        </div>
      )}

      {paragraphs.length === 0 &&
        isConnected &&
        !showReconnecting &&
        !showExhausted && (
          <p className="text-sm text-muted-foreground">
            {showNoSpeechCopy
              ? t(
                  "RECORD.LIVE_NO_SPEECH_DETECTED",
                  "We haven’t heard speech yet. Check your mic input level.",
                )
              : t("RECORD.LIVE_LISTENING", "Listening for speech…")}
          </p>
        )}

      {paragraphs.map((paragraph) => {
        return (
          <div key={paragraph.id} className="flex gap-3" style={speakerHueStyle(paragraph.speakerId)}>
            <span
              aria-hidden="true"
              className="speaker-mark mt-1 w-0.5 flex-none self-stretch rounded-full"
            />
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                {paragraph.speakerName && (
                  <span className="speaker-text text-xs font-semibold">
                    {paragraph.speakerName}
                  </span>
                )}
                {paragraph.startTime > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(paragraph.startTime)}
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed text-foreground">
                {paragraph.text}
              </p>
            </div>
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
}
